import clsx from "clsx";
import { useMutation } from "@tanstack/react-query";
import { LogOut, Users } from "lucide-react";
import type { MouseEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSession } from "../../app/AppProviders.js";
import { LOGIN_PATH } from "../../app/RequireSession.js";
import { signOutFailureMessage } from "../../ui/AsyncPanel.js";
import { Button } from "../../ui/Button.js";
import { roleLabel } from "../cases/parts-actions/RecipientWarning.js";
import {
  WHOLESALE_DESTINATIONS,
  WHOLESALE_PATH,
  type WholesalePageDestination,
  type WholesaleView,
} from "./presentation.js";
import { SideSwitch } from "./SideSwitch.js";
import { serializeWholesaleUrlState, type WholesaleUrlState } from "./useWholesaleUrlState.js";

/**
 * Left navigation of the wholesale side (spec §4.1, §4.2, §4.6): the
 * Service | Wholesale switch, New wholesale order, Open, Arrived · 36h,
 * History, All orders, Search, Customers, then the Stock order report and
 * the Parts catalog both sides share — plus who is signed in and Sign out.
 *
 * It is the service sidebar's twin: every destination is a real link to
 * the list's address (a new tab, the status bar, a reload all work), a
 * plain click goes through `onSelectView` for the same URL-state reason
 * (a search still being typed commits on the blur the click causes), and
 * choosing a list keeps the search and the customer filter (Search takes
 * no customer — its route knows none) and drops the selected order. Below
 * 1280 CSS pixels the shell folds it into a menu.
 */

export interface WholesaleSidebarProps {
  id: string;
  open: boolean;
  state: WholesaleUrlState;
  /** Which destination is the current page; defaults to the list in `state`. */
  activeDestination?: WholesaleView | WholesalePageDestination;
  onSelectView: (view: WholesaleView) => void;
  onNavigate?: () => void;
  className?: string;
}

function isPlainClick(event: MouseEvent<HTMLAnchorElement>): boolean {
  return (
    event.button === 0 &&
    !event.metaKey &&
    !event.altKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.defaultPrevented &&
    (!event.currentTarget.target || event.currentTarget.target === "_self")
  );
}

/** The address of `view` with the current search and customer and no selection. */
export function wholesaleViewPath(state: WholesaleUrlState, view: WholesaleView): string {
  const params = serializeWholesaleUrlState({ ...state, view, orderId: null });
  return `${WHOLESALE_PATH}?${params.toString()}`;
}

export function WholesaleSidebar({
  id,
  open,
  state,
  activeDestination = state.view,
  onSelectView,
  onNavigate,
  className,
}: WholesaleSidebarProps) {
  const { user, logout } = useSession();
  const navigate = useNavigate();
  const signOut = useMutation({
    mutationFn: logout,
    onSuccess: () => navigate(LOGIN_PATH, { replace: true }),
  });

  return (
    <nav
      id={id}
      className={clsx("workspace__sidebar", className)}
      aria-label="Wholesale navigation"
      data-open={open ? "true" : "false"}
    >
      <div className="sidebar__brand">
        <span className="sidebar__brand-name">Chicagoland Auto Tools</span>
        <span className="sidebar__brand-role">Wholesale</span>
      </div>

      <SideSwitch side="wholesale" onNavigate={onNavigate} />

      <ul className="sidebar__destinations">
        {WHOLESALE_DESTINATIONS.map((destination) => {
          const Icon = destination.Icon;
          if (destination.kind !== "view") {
            return (
              <li
                key={destination.kind}
                className={clsx(
                  "sidebar__item",
                  destination.kind === "new_order" && "sidebar__item--action",
                  destination.kind === "stock_order_report" && "sidebar__item--page",
                )}
              >
                <Link
                  to={destination.to}
                  className={clsx("sidebar__link", destination.kind === "new_order" && "sidebar__link--action")}
                  aria-current={activeDestination === destination.kind ? "page" : undefined}
                  onClick={onNavigate}
                >
                  <Icon className="sidebar__icon" aria-hidden="true" focusable="false" />
                  <span className="sidebar__label">{destination.label}</span>
                </Link>
              </li>
            );
          }
          const active = destination.view === activeDestination;
          return (
            <li key={destination.view} className="sidebar__item">
              <Link
                to={wholesaleViewPath(state, destination.view)}
                className="sidebar__link"
                aria-current={active ? "page" : undefined}
                onClick={(event) => {
                  if (isPlainClick(event)) {
                    event.preventDefault();
                    onSelectView(destination.view);
                  }
                  onNavigate?.();
                }}
              >
                <Icon className="sidebar__icon" aria-hidden="true" focusable="false" />
                <span className="sidebar__label">{destination.label}</span>
              </Link>
            </li>
          );
        })}
      </ul>

      <div className="sidebar__session">
        {user ? (
          <p className="sidebar__user">
            <span className="visually-hidden">Signed in as </span>
            <span className="sidebar__user-name">{user.displayName}</span>
            <span className="sidebar__user-role">{roleLabel(user.role)}</span>
          </p>
        ) : null}
        {user?.isAdmin ? (
          <Link to="/accounts" className="sidebar__admin-link">
            <Users aria-hidden="true" />
            Accounts
          </Link>
        ) : null}
        {signOut.isError ? (
          <p role="alert" className="form-alert sidebar__alert">
            {signOutFailureMessage(signOut.error)}
          </p>
        ) : null}
        <Button
          variant="quiet"
          icon={<LogOut />}
          busy={signOut.isPending}
          onClick={() => signOut.mutate()}
          className="sidebar__sign-out"
        >
          Sign out
        </Button>
      </div>
    </nav>
  );
}
